// frontend/src/pages/customer/Checkout.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiShoppingCart,
  FiCreditCard,
  FiTruck,
  FiCheckCircle,
  FiAlertCircle,
  FiArrowLeft
} from 'react-icons/fi';

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  
  const [cartItems, setCartItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    payment_method: 'bank_transfer',
    bank_account_id: '',
    delivery_address: user?.address || '',
    delivery_phone: user?.phone || '',
    delivery_note: '',
    notes: ''
  });

  useEffect(() => {
    loadCart();
    fetchBankAccounts();
  }, []);

  const loadCart = async () => {
    try {
      const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
      
      if (cart.length === 0) {
        toast.info('Сагс хоосон байна');
        navigate('/customer/cart');
        return;
      }

      const response = await axios.post('/api/customer/cart/validate', {
        items: cart
      });

      if (response.data.success) {
        setCartItems(response.data.data.items);
      }
    } catch (error) {
      console.error('Load cart error:', error);
      toast.error('Сагс ачаалахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get('/api/customer/bank-accounts');
      if (response.data.success) {
        setBankAccounts(response.data.data.accounts);
        
        // Auto-select first bank account
        if (response.data.data.accounts.length > 0) {
          setFormData(prev => ({
            ...prev,
            bank_account_id: response.data.data.accounts[0].id
          }));
        }
      }
    } catch (error) {
      console.error('Fetch bank accounts error:', error);
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');

    cartItems.forEach(item => {
      const cartItem = cart.find(c => c.productId === item.id);
      if (cartItem) {
        const quantity = cartItem.quantity;
        const isWholesale = user?.customer_type === 'wholesale' && 
                           quantity >= item.wholesale_quantity &&
                           item.wholesale_price > 0;
        const price = isWholesale ? item.wholesale_price : item.retail_price;
        subtotal += price * quantity;
      }
    });

    // ✅ Хөнгөлөлт тооцох
    const discountRate = user?.discount_rate || 0;
    const discountAmount = subtotal * (discountRate / 100);
    const afterDiscount = subtotal - discountAmount;

    // ✅ НӨАТ тооцох (tax_exempt шалгах)
    const taxExempt = user?.tax_exempt || false;
    const tax = taxExempt ? 0 : afterDiscount * 0.1;
    const total = afterDiscount + tax;

    return { 
      subtotal, 
      discountRate, 
      discountAmount, 
      afterDiscount,
      taxExempt,
      tax, 
      total 
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Сагс хоосон байна');
      return;
    }

    if (formData.payment_method === 'bank_transfer' && !formData.bank_account_id) {
      toast.error('Банкны данс сонгоно уу');
      return;
    }

    setSubmitting(true);

    try {
      const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
      const { total } = calculateTotals();

      // Prepare order items
      const items = cartItems.map(item => {
        const cartItem = cart.find(c => c.productId === item.id);
        const quantity = cartItem.quantity;
        const isWholesale = user?.customer_type === 'wholesale' && 
                           quantity >= item.wholesale_quantity &&
                           item.wholesale_price > 0;
        const unit_price = isWholesale ? item.wholesale_price : item.retail_price;

        return {
          product_id: item.id,
          quantity: quantity,
          unit_price: unit_price
        };
      });

      const response = await axios.post('/api/customer/orders', {
        items,
        total_amount: total,
        payment_method: formData.payment_method,
        bank_account_id: formData.bank_account_id || null,
        delivery_address: formData.delivery_address,
        delivery_phone: formData.delivery_phone,
        delivery_note: formData.delivery_note,
        notes: formData.notes
      });

      if (response.data.success) {
        // Clear cart
        localStorage.removeItem('customerCart');
        
        toast.success('Захиалга амжилттай үүслээ!');
        
        // Redirect to order detail
        navigate(`/customer/orders/${response.data.data.order.id}`);
      }
    } catch (error) {
      console.error('Create order error:', error);
      toast.error(error.response?.data?.message || 'Захиалга үүсгэхэд алдаа гарлаа');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <div className="spinner-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  const { subtotal, discountRate, discountAmount, afterDiscount, taxExempt, tax, total } = calculateTotals();
  const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');

  return (
    <div className="checkout-page">
      <div className="container-custom py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/customer/cart')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft />
            <span>Буцах</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Захиалга баталгаажуулах</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Delivery Info */}
              <div className="card">
                <div className="flex items-center gap-2 mb-4">
                  <FiTruck className="text-blue-600" size={20} />
                  <h2 className="text-lg font-semibold text-gray-900">Хүргэлтийн мэдээлэл</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Хаяг <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Утас <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.delivery_phone}
                      onChange={(e) => setFormData({ ...formData, delivery_phone: e.target.value })}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Нэмэлт тэмдэглэл
                    </label>
                    <textarea
                      value={formData.delivery_note}
                      onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })}
                      className="input"
                      rows="3"
                      placeholder="Хүргэлтийн нэмэлт мэдээлэл"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="card">
                <div className="flex items-center gap-2 mb-4">
                  <FiCreditCard className="text-blue-600" size={20} />
                  <h2 className="text-lg font-semibold text-gray-900">Төлбөрийн хэлбэр</h2>
                </div>

                <div className="space-y-4">
                  {/* Bank Transfer */}
                  <div>
                    <label className="flex items-center gap-3 p-4 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer">
                      <input
                        type="radio"
                        name="payment_method"
                        value="bank_transfer"
                        checked={formData.payment_method === 'bank_transfer'}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Банкны шилжүүлэг</p>
                        <p className="text-sm text-gray-600">Манай дансруу шилжүүлэх</p>
                      </div>
                    </label>

                    {formData.payment_method === 'bank_transfer' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Банкны данс сонгох <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.bank_account_id}
                          onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                          className="input"
                          required
                        >
                          <option value="">Данс сонгох</option>
                          {bankAccounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.bank_name} - {account.account_number} ({account.account_name})
                            </option>
                          ))}
                        </select>

                        {formData.bank_account_id && (
                          <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <FiAlertCircle className="text-yellow-600 mt-0.5" />
                              <div className="text-sm text-yellow-800">
                                <p className="font-medium mb-1">Төлбөрийн заавар:</p>
                                <p>1. Дээрх данс руу захиалгын дүнг шилжүүлнэ үү</p>
                                <p>2. Гүйлгээний утгад захиалгын дугаарыг бичнэ</p>
                                <p>3. "Захиалах" товч дарж захиалгаа үүсгэнэ үү</p>
                                <p className="mt-2 text-yellow-900 font-medium">
                                  Админ таны төлбөрийг баталгаажуулсны дараа захиалга идэвхжинэ
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Credit */}
                  {user?.customer_type === 'wholesale' && user?.credit_limit > 0 && (
                    <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-300">
                      <input
                        type="radio"
                        name="payment_method"
                        value="credit"
                        checked={formData.payment_method === 'credit'}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Зээлээр</p>
                        <p className="text-sm text-gray-600">
                          Боломжит зээл: ₮{user.credit_limit.toLocaleString()}
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="card">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Захиалгын тэмдэглэл
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Нэмэлт тэмдэглэл, хүсэлт..."
                />
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div>
              <div className="card sticky top-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Захиалгын дүн</h3>

                {/* Items */}
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {cartItems.map(item => {
                    const cartItem = cart.find(c => c.productId === item.id);
                    const quantity = cartItem?.quantity || 0;
                    const isWholesale = user?.customer_type === 'wholesale' && 
                                       quantity >= item.wholesale_quantity &&
                                       item.wholesale_price > 0;
                    const price = isWholesale ? item.wholesale_price : item.retail_price;

                    return (
                      <div key={item.id} className="flex items-center gap-3 pb-3 border-b">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {quantity} × ₮{price.toLocaleString()}
                            {isWholesale && <span className="text-blue-600 ml-1">(Бөөн)</span>}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          ₮{(price * quantity).toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div className="space-y-2 py-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Дэд дүн:</span>
                    <span className="font-medium">₮{subtotal.toLocaleString()}</span>
                  </div>

                  {/* ✅ Хөнгөлөлт */}
                  {discountRate > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Хөнгөлөлт ({discountRate}%):</span>
                        <span className="font-semibold">-₮{discountAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Хөнгөлөлтийн дараа:</span>
                        <span className="font-medium">₮{afterDiscount.toLocaleString()}</span>
                      </div>
                    </>
                  )}

                  {/* ✅ НӨАТ - Сайжруулсан */}
                  {taxExempt ? (
                    <div className="flex justify-between items-center bg-green-50 -mx-4 px-4 py-2 rounded">
                      <span className="text-gray-700 text-sm">НӨАТ:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-semibold text-sm">₮0</span>
                        <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          Чөлөөлөгдсөн
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">НӨАТ (10%):</span>
                      <span className="font-medium">₮{tax.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Нийт төлөх:</span>
                    <span className="text-blue-600">₮{total.toLocaleString()}</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || cartItems.length === 0}
                  className="btn btn-primary w-full"
                >
                  {submitting ? (
                    <>
                      <div className="spinner-sm mr-2"></div>
                      Уншиж байна...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2" />
                      Захиалах
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Захиалах товч дарснаар та манай <a href="#" className="text-blue-600 hover:underline">үйлчилгээний нөхцөл</a>-ийг зөвшөөрч байна
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;